import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Button, Col, Container, ProgressBar, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import TrackMatchingVideo from "./TrackMatchingVideo";
import { matchApi } from "@/api";
import Preloader from "@/components/Elements/Preloader";
import { continueVideoProcessing } from "@/store/dashboardSlice";
import { useDispatch } from "react-redux";

const TrackMatching = () => {
  const { videoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [match, setMatch] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [task, setTask] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [blockedCount, setBlockedCount] = useState(0);
  const [fullCount, setFullCount] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [candidateId, setCandidateId] = useState(null);
  const [progress, setProgress] = useState("");
  const [previousTaskId, setPreviousTaskId] = useState(null);

  useEffect(() => {
    async function fetchTask() {
      try {
        setIsLoading(true);
        await fetchMatch();
        await fetchMatchTask();
      } catch (error) {
        setIsError(true);
        toast(error.message);
      } finally {
        setIsLoading(false);
      }
    }
    fetchTask();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", keyDownHandler, false);

    return () => {
      document.removeEventListener("keydown", keyDownHandler, false);
    };
  }, [videoId, taskId, task, selectedTrackId, location]);

  useEffect(() => {
    setProgress(`${blockedCount}/${fullCount}`);
  }, [blockedCount, fullCount]);

  const fetchMatch = async () => {
    const match = await matchApi.getMatch(videoId);
    setMatch(match);
  };

  const fetchMatchTask = async (
    isNewFetchNewTask = false,
    previousTaskId = null
  ) => {
    let response = null;
    const query = new URLSearchParams(location.search);
    const targetTaskId = previousTaskId || query.get("taskId");

    try {
      if (targetTaskId && !isNewFetchNewTask) {
        response = await matchApi.getMatchTask(videoId, targetTaskId);
      } else {
        response = await matchApi.postMatchTask(videoId);
      }

      const { task, id, blocked_count, full_count, result } = response;
      if (task && id) {
        setTask(task);
        setTaskId(id);
        setSelectedTrackId(null);
        setBlockedCount(blocked_count);
        setFullCount(full_count);
        setIsSubmit(false);
        setCandidateId(Number(result?.candidate));
        // TODO: this prevents us from going backward directly to the match page.
        // It goes to /panel/markup/track/${videoId} instead of /dashboard
        navigate(`/panel/markup/track/${videoId}?taskId=${id}`);
      } else {
        setIsError(true);
        toast.error("There are no tasks for this match");
        // await postVideoContinue();
        navigate("/panel");
      }
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  async function sendMatchTask(isTrackNotFound = false) {
    setIsLoading(true);
    setIsSubmit(true);
    try {
      setPreviousTaskId(taskId);
      await matchApi.postMatchTaskResult({
        matchId: videoId,
        taskId: taskId,
        taskResult: {
          task_id: task.task_id,
          lost_track_id: task.lost_track_id,
          candidate: isTrackNotFound ? -1 : selectedTrackId ?? "",
        },
        taskType: "track",
      });
      if (isTrackNotFound) {
        toast.success("Track deleted");
      } else {
        toast.success("Result sent");
      }
      await fetchMatchTask(true);
    } catch (error) {
      setIsError(true);
      toast.error(error.message);
    } finally {
      setIsError(false);
    }
  }

  function updateSelectedTrack(trackId) {
    if (trackId === selectedTrackId) {
      setSelectedTrackId(null);
    } else {
      setSelectedTrackId(trackId);
    }
  }

  function getNextTrackIdByDirection(direction) {
    const tracks = task.candidates;

    if (!tracks.includes(selectedTrackId)) {
      updateSelectedTrack(tracks.at(0));

      return;
    }

    const currentTrackIndex = tracks.findIndex((id) => selectedTrackId === id);

    const nextTrackIndex =
      direction > 0 ? currentTrackIndex + 1 : currentTrackIndex - 1;
    if (nextTrackIndex >= tracks.length) {
      updateSelectedTrack(tracks.at(0));
    } else if (nextTrackIndex < 0) {
      updateSelectedTrack(tracks.at(-1));
    } else {
      updateSelectedTrack(tracks.at(nextTrackIndex));
    }
  }

  async function keyDownHandler(e) {
    const { code, ctrlKey, metaKey } = e;
    if (code === "Enter" || code === "KeyW") {
      e.preventDefault();
      await sendMatchTask();
    } else if (code === "ArrowLeft") {
      e.preventDefault();
      getNextTrackIdByDirection(false);
    } else if (code === "ArrowRight") {
      e.preventDefault();
      getNextTrackIdByDirection(true);
    } else if (code === "KeyA") {
      e.preventDefault();
      await sendMatchTask(true);
    } else if (code === "Space") {
      e.preventDefault();
    } else if (code === "KeyZ" && (ctrlKey || metaKey)) {
      e.preventDefault();
      if (previousTaskId) await fetchMatchTask(false, previousTaskId);
    }
  }

  return (
    <>
      {match && task ? (
        <Container className="py-3" fluid>
          {fullCount && (
            <Row className="justify-content-center mb-3">
              <Col xs={4}>
                <div>
                  Markup progress {blockedCount}/{fullCount}
                </div>
                <ProgressBar
                  min={0}
                  max={fullCount}
                  now={blockedCount}
                  label={progress}
                />
              </Col>
            </Row>
          )}
          <Row className="justify-content-center mb-1">
            <Col xs={4}>
              <div>Task number {task.task_id}</div>
            </Col>
          </Row>
          <Row>
            <Col>
              <TrackMatchingVideo
                src={match?.video.origin}
                task={task}
                candidateId={candidateId}
                selectedTrack={selectedTrackId}
                updateSelectedTrack={updateSelectedTrack}
                isSubmit={isSubmit}
              />
            </Col>
          </Row>
          <Row className="justify-content-center mt-2">
            <Col xs={"auto"}>
              <Button onClick={() => sendMatchTask(false)}>
                {selectedTrackId ? "Submit selection" : "No required track"}
              </Button>

              <Button
                className={"mx-2"}
                variant="outline-primary"
                onClick={() => sendMatchTask(true)}
              >
                Delete track
              </Button>
            </Col>
          </Row>
        </Container>
      ) : isError ? (
        <Row className="justify-content-center text-center mt-5">
          <Col>Error! Please try again later</Col>
        </Row>
      ) : (
        <Preloader className="m-auto" />
      )}
    </>
  );
};

export default TrackMatching;
